import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { defaultCodedProperty, defaultNamedProperty } from "sprint-common-web";

import { OnPremOrderSource } from "../../../../common/api/orders-api/on-prem-order/GetOnPremOrderListResponse";
import { useCachedOnPremOrdersApi } from "../../../../common/api/orders-api/useCachedOnPremOrdersApi";
import { ContentReferenceType } from "../../../../common/types/ContentReferenceType";
import { ContentSubtype } from "../../../../common/types/ContentSubtype";
import { OnPremOrderStatus } from "../../../common/types/OnPremOrderStatus";
import { OrderAssetAvailability } from "../../../common/types/OrderAssetAvailability";
import { OnPremOrderDashboardItem, mapToOnPremOrderDashboardItem } from "../OnPremOrderDashboardItem";
import { useOnPremOrderAssetNotes } from "./useOnPremOrderAssetNotes";

export const useGetOnPremOrderDashboard = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [onPremOrderDetails, setOnPremOrderDetails] = useState<OnPremOrderDashboardItem>(
        defaultOnPremOrderDashboardItem
    );
    const { orderId } = useParams<{ orderId: string }>();

    const { getOnPremOrderDetails } = useCachedOnPremOrdersApi();
    const { notes, areNotesLoading } = useOnPremOrderAssetNotes({ onPremOrder: onPremOrderDetails });

    useEffect(() => {
        fetchOnPremOrderDetails();
    }, []);

    useEffect(() => {
        if (!notes?.length) {
            return;
        }

        setOnPremOrderDetails((prevState) => ({ ...prevState, assetNotes: notes }));
    }, [notes]);

    const fetchOnPremOrderDetails = () => {
        setIsLoading(true);

        getOnPremOrderDetails({ orderId })
            .then((orderDetailsResponse) => {
                setOnPremOrderDetails(mapToOnPremOrderDashboardItem(orderDetailsResponse.onPremOrder));
            })
            .finally(() => setIsLoading(false));
    };

    const updateOnPremOrderDetails = (item: Partial<OnPremOrderDashboardItem>) => {
        setOnPremOrderDetails((prevState) => ({ ...prevState, ...item }));
    };

    return {
        isLoading: isLoading,
        areNotesLoading,
        onPremOrderDetails,
        fetchOnPremOrderDetails,
        updateOnPremOrderDetails
    };
};

const defaultOnPremOrderDashboardItem: OnPremOrderDashboardItem = {
    id: "",
    pONumber: "",
    content: {
        type: ContentReferenceType.Movie,
        movie: { id: "", title: "" },
        subtype: ContentSubtype.Movie,
        version: { id: "", type: defaultNamedProperty, distributor: defaultNamedProperty }
    },
    airline: defaultCodedProperty,
    system: defaultNamedProperty,
    dueDate: "",
    startDate: "",
    onPremAssets: {
        video: {
            availability: OrderAssetAvailability.NoMatch,
            targetFilename: "",
            aspectRatio: defaultNamedProperty,
            sourceResolution: { width: 0, height: 0 },
            targetResolution: { width: 0, height: 0 },
            subtitle: defaultCodedProperty,
            nonMatchingVideos: []
        },
        soundtracks: [],
        subtitles: []
    },
    status: OnPremOrderStatus.Ordered,
    source: OnPremOrderSource.Buzz,
    materialSpecification: {},
    assetNotes: []
};
